import { useAdminFeatures } from '@lib/features/useFeatureAdmin'
import {
  DEFAULT_TENANT_TYPE_TAB,
  TENANT_TYPES,
  getTenantTypeLabel,
  tabValueToTenantType,
  tenantTypeToTabValue
} from '@lib/tenantTypes'
import {
  useIcpBillingProfiles,
  usePlanCapMatrix,
  usePlanFeatureMatrix,
  useStripeAdminActions
} from '@lib/stripe/useStripeAdmin'
import {
  Alert,
  Badge,
  Checkbox,
  Grid,
  Group,
  NumberInput,
  Select,
  Stack,
  Tabs,
  Text
} from '@mantine/core'
import { H1, H3, PageContentPaper, Button } from '@packages/ui'
import { useMemo, useState, useEffect } from 'react'
import { mutate } from 'swr'

const BILLING_MODELS = [
  { value: 'package_tenant', label: 'Package (Hub)' },
  { value: 'per_seat_plans', label: 'Per-seat plans' },
  { value: 'metered_usage', label: 'Metered usage' },
  { value: 'none', label: 'None' }
]

const PLAN_TIERS = ['basic', 'pro', 'elite', 'hub', 'nest', 'hive']

const CAP_DEFS: Record<
  string,
  { key: string; label: string; placeholder?: string }[]
> = {
  per_seat_plans: [
    {
      key: 'membership.worker',
      label: 'Team members (active + pending)',
      placeholder: 'Unlimited'
    }
  ],
  package_tenant: [
    { key: 'entity.person_in_care', label: 'People in care' },
    { key: 'entity.supporter', label: 'Supporters' }
  ]
}

export const PlanManagement = () => {
  const { data: profiles = [], error: profilesError } = useIcpBillingProfiles()
  const { data: matrix = [], error: matrixError } = usePlanFeatureMatrix()
  const { data: capMatrix = [], error: capMatrixError } = usePlanCapMatrix()
  const { data: features = [] } = useAdminFeatures()
  const { upsertIcpProfile, setPlanFeatures, setPlanCaps, refreshIcpTenantClaims } =
    useStripeAdminActions()

  const activeTypeKeys = useMemo(
    () => TENANT_TYPES.map((t) => t.value),
    []
  )
  const [selectedTabValue, setSelectedTabValue] = useState(() =>
    tenantTypeToTabValue(DEFAULT_TENANT_TYPE_TAB)
  )
  const selectedTenantType = tabValueToTenantType(selectedTabValue)

  const profile = profiles.find((p) => p.tenant_type === selectedTenantType)
  const [billingModel, setBillingModel] = useState<string>(
    profile?.billing_model ?? 'per_seat_plans'
  )
  const [profileEnabled, setProfileEnabled] = useState(profile?.enabled ?? true)
  const [savingProfile, setSavingProfile] = useState(false)

  useEffect(() => {
    if (!profile) return
    setBillingModel(profile.billing_model)
    setProfileEnabled(profile.enabled)
  }, [profile?.tenant_type, profile?.billing_model, profile?.enabled])

  const matrixForType = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const row of matrix) {
      if (row.tenant_type !== selectedTenantType) continue
      if (!map.has(row.plan_tier)) map.set(row.plan_tier, new Set())
      map.get(row.plan_tier)!.add(row.feature_key)
    }
    return map
  }, [matrix, selectedTenantType])

  const capsForType = useMemo(() => {
    const map = new Map<string, Record<string, number | null>>()
    for (const row of capMatrix) {
      if (row.tenant_type !== selectedTenantType) continue
      if (!map.has(row.plan_tier)) map.set(row.plan_tier, {})
      map.get(row.plan_tier)![row.cap_key] = row.max_value
    }
    return map
  }, [capMatrix, selectedTenantType])

  const capDefs = CAP_DEFS[billingModel] ?? []

  const [planDrafts, setPlanDrafts] = useState<Record<string, Set<string>>>({})
  const [capDrafts, setCapDrafts] = useState<
    Record<string, Record<string, number | null>>
  >({})
  const [savingPlan, setSavingPlan] = useState<string | null>(null)
  const [savingCaps, setSavingCaps] = useState<string | null>(null)
  const [refreshPlanTier, setRefreshPlanTier] = useState<string | null>(null)
  const [refreshingClaims, setRefreshingClaims] = useState(false)
  const [refreshResult, setRefreshResult] = useState<string | null>(null)

  const getPlanKeys = (tier: string) => {
    if (planDrafts[tier]) return planDrafts[tier]
    return matrixForType.get(tier) ?? new Set<string>()
  }

  const togglePlanFeature = (tier: string, featureKey: string) => {
    const current = new Set(getPlanKeys(tier))
    if (current.has(featureKey)) current.delete(featureKey)
    else current.add(featureKey)
    setPlanDrafts((d) => ({ ...d, [tier]: current }))
  }

  const getCapValues = (tier: string) => {
    if (capDrafts[tier]) return capDrafts[tier]
    return capsForType.get(tier) ?? {}
  }

  const setCapValue = (tier: string, capKey: string, value: number | null) => {
    setCapDrafts((d) => ({
      ...d,
      [tier]: { ...getCapValues(tier), [capKey]: value }
    }))
  }

  const saveCaps = async (tier: string) => {
    setSavingCaps(tier)
    try {
      const values = getCapValues(tier)
      const payload: Record<string, number | null> = {}
      for (const def of capDefs) {
        payload[def.key] = values[def.key] ?? null
      }
      await setPlanCaps(selectedTenantType, tier, payload)
      setCapDrafts((d) => {
        const next = { ...d }
        delete next[tier]
        return next
      })
      await mutate('admin-plan-cap-matrix')
      setRefreshResult(`Saved ${tier} plan caps.`)
    } finally {
      setSavingCaps(null)
    }
  }

  const saveProfile = async () => {
    setSavingProfile(true)
    try {
      await upsertIcpProfile({
        tenantType: selectedTenantType,
        billingModel: billingModel as never,
        enabled: profileEnabled,
        description: profile?.description
      })
      await mutate('admin-icp-billing-profiles')
    } finally {
      setSavingProfile(false)
    }
  }

  const savePlan = async (tier: string) => {
    setSavingPlan(tier)
    try {
      const result = await setPlanFeatures(
        selectedTenantType,
        tier,
        Array.from(getPlanKeys(tier))
      )
      setPlanDrafts((d) => {
        const next = { ...d }
        delete next[tier]
        return next
      })
      await mutate('admin-plan-feature-matrix')
      if (result?.tenants_refreshed != null) {
        setRefreshResult(
          `Saved ${tier}: refreshed JWT for ${result.tenants_refreshed} tenant(s), ${result.users_refreshed ?? 0} user(s).`
        )
      }
    } finally {
      setSavingPlan(null)
    }
  }

  const visibleTiers =
    billingModel === 'package_tenant'
      ? ['hub', 'nest', 'hive']
      : billingModel === 'per_seat_plans'
        ? ['basic', 'pro', 'elite']
        : []

  return (
    <Stack c="white" gap="lg">
      <H1 className="text-white">Plan management</H1>
      <Text c="dimmed" size="sm">
        ICP billing profiles and plan defaults (features and caps). Per-tenant
        overrides are on each tenant&apos;s Features tab. Module ceilings stay
        under Features.
      </Text>

      {(profilesError || matrixError || capMatrixError) && (
        <Alert color="red" title="Load error">
          {profilesError?.message ??
            matrixError?.message ??
            capMatrixError?.message}
        </Alert>
      )}

      <Tabs value={selectedTabValue} onChange={(v) => v && setSelectedTabValue(v)}>
        <Tabs.List>
          {activeTypeKeys.map((tenantType) => (
            <Tabs.Tab
              key={tenantTypeToTabValue(tenantType)}
              value={tenantTypeToTabValue(tenantType)}
            >
              {getTenantTypeLabel(tenantType)}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {activeTypeKeys.map((tenantType) => (
          <Tabs.Panel
            key={tenantTypeToTabValue(tenantType)}
            value={tenantTypeToTabValue(tenantType)}
            pt="md"
          >
            <Stack gap="lg">
              <PageContentPaper>
                <Stack gap="md">
                  <H3>ICP billing profile</H3>
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 4 }}>
                      <Select
                        label="Billing model"
                        data={BILLING_MODELS}
                        value={billingModel}
                        onChange={(v) => v && setBillingModel(v)}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 4 }}>
                      <Checkbox
                        mt="xl"
                        label="Billing enabled for this ICP"
                        checked={profileEnabled}
                        onChange={(e) =>
                          setProfileEnabled(e.currentTarget.checked)
                        }
                      />
                    </Grid.Col>
                  </Grid>
                  <Group>
                    <Button loading={savingProfile} onClick={saveProfile}>
                      Save profile
                    </Button>
                    {profile && (
                      <Badge variant="light">{profile.billing_model}</Badge>
                    )}
                  </Group>
                </Stack>
              </PageContentPaper>

              {visibleTiers.length > 0 && (
                <PageContentPaper>
                  <Stack gap="md">
                    <H3>Plan → features (defaults)</H3>
                    <Text size="sm" c="dimmed">
                      Tenant-wide plan tier selects which row applies. Per-tenant
                      customizations are on the tenant Features tab.
                    </Text>
                    {visibleTiers.map((tier) => (
                      <Stack key={tier} gap="xs">
                        <Group justify="space-between">
                          <Text fw={600} tt="capitalize">
                            {tier}
                          </Text>
                          <Button
                            size="xs"
                            variant="light"
                            loading={savingPlan === tier}
                            onClick={() => savePlan(tier)}
                          >
                            Save {tier}
                          </Button>
                        </Group>
                        <Group gap="md">
                          {features.map((f) => (
                            <Checkbox
                              key={`${tier}-${f.key}`}
                              label={f.key}
                              checked={getPlanKeys(tier).has(f.key)}
                              onChange={() => togglePlanFeature(tier, f.key)}
                            />
                          ))}
                        </Group>
                      </Stack>
                    ))}
                  </Stack>
                </PageContentPaper>
              )}

              {visibleTiers.length > 0 && capDefs.length > 0 && (
                <PageContentPaper data-testid="plan-cap-matrix">
                  <Stack gap="md">
                    <H3>Plan → caps (limits)</H3>
                    <Text size="sm" c="dimmed">
                      Empty = unlimited. Facility counts active + pending invites.
                      Changes apply immediately via plan context (no JWT refresh).
                    </Text>
                    {visibleTiers.map((tier) => (
                      <Stack key={`cap-${tier}`} gap="xs">
                        <Group justify="space-between">
                          <Text fw={600} tt="capitalize">
                            {tier}
                          </Text>
                          <Button
                            size="xs"
                            variant="light"
                            loading={savingCaps === tier}
                            data-testid={`plan-cap-save-${tier}`}
                            onClick={() => saveCaps(tier)}
                          >
                            Save {tier} caps
                          </Button>
                        </Group>
                        <Group gap="md" align="flex-end">
                          {capDefs.map((def) => (
                            <NumberInput
                              key={`${tier}-${def.key}`}
                              label={def.label}
                              placeholder={def.placeholder ?? 'Unlimited'}
                              min={1}
                              allowDecimal={false}
                              allowNegative={false}
                              value={getCapValues(tier)[def.key] ?? ''}
                              onChange={(v) =>
                                setCapValue(
                                  tier,
                                  def.key,
                                  v === '' || v == null ? null : Number(v)
                                )
                              }
                              w={220}
                              data-testid={`plan-cap-${tier}-${def.key.replace(/\./g, '-')}`}
                            />
                          ))}
                        </Group>
                      </Stack>
                    ))}
                  </Stack>
                </PageContentPaper>
              )}

              {visibleTiers.length > 0 && (
                <PageContentPaper>
                  <Stack gap="md">
                    <H3>Apply plan matrix to tenants</H3>
                    <Text size="sm" c="dimmed">
                      After editing plan → feature defaults, refresh JWT features
                      for existing tenants. Does not clear per-tenant overrides.
                    </Text>
                    <Group align="flex-end">
                      <Select
                        label="Plan tier filter"
                        placeholder="All tiers"
                        clearable
                        data={visibleTiers.map((t) => ({
                          value: t,
                          label: t.charAt(0).toUpperCase() + t.slice(1)
                        }))}
                        value={refreshPlanTier}
                        onChange={setRefreshPlanTier}
                        w={220}
                      />
                      <Button
                        loading={refreshingClaims}
                        onClick={async () => {
                          setRefreshingClaims(true)
                          setRefreshResult(null)
                          try {
                            const result = await refreshIcpTenantClaims(
                              selectedTenantType,
                              refreshPlanTier
                            )
                            setRefreshResult(
                              `Refreshed ${result.tenants_refreshed} tenant(s), ${result.users_refreshed} user session(s).`
                            )
                          } catch (err) {
                            setRefreshResult(
                              err instanceof Error ? err.message : 'Refresh failed'
                            )
                          } finally {
                            setRefreshingClaims(false)
                          }
                        }}
                      >
                        Refresh JWT for ICP tenants
                      </Button>
                    </Group>
                    {refreshResult && (
                      <Text size="sm" c="dimmed">
                        {refreshResult}
                      </Text>
                    )}
                  </Stack>
                </PageContentPaper>
              )}

            </Stack>
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  )
}
